/**
 * Modal for resolving a label-generation error. It owns everything the flow shares: the header -
 * the flow title on the screen the modal opens on, Back on every screen that has a parent - the
 * help links, and the table of screens. A screen renders its own content and nothing else.
 */
import { observer } from "mobx-react";
import AHref from "../../../../../components/ahref/ahref";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import FontHeading4 from "../../../../../components/fonts/font-heading4/font-heading4";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import IconBack from "../../../../../components/icons/icon-back/icon-back";
import IconNewTab from "../../../../../components/icons/icon-new-tab/icon-new-tab";
import Icon from "../../../../../components/icons/icon/icon";
import { DefaultModalLayout } from "../../../../../components/modal/layouts/default-modal-layout";
import {
    IResolveLabelErrorScreenProps,
    RESOLVE_LABEL_ERROR_STEP,
} from "../../stores/rma-action-store/resolve-label-error-action-store";
import { IRmaResolveLabelErrorAction } from "../../stores/rma-page-store";
import ChangeShippingOptionScreen from "./screens/change-shipping-option/change-shipping-option-screen";
import SelectResolutionPathScreen from "./screens/select-resolution-path/select-resolution-path-screen";
import SelectShippingRateScreen from "./screens/select-shipping-rate/select-shipping-rate-screen";
import ValidateShippingDetailsScreen from "./screens/validate-shipping-details/validate-shipping-details-screen";
import "./resolve-label-error-modal.scss";

const KNOWLEDGE_BASE_URL = "https://returngo.ai/go/knowledge-base/edit-shipment-details";
const SUPPORT_URL = "https://support.returngo.ai/contacting-returngo-support";
const HEADER_ICON_COLOR = "var(--color-neutral-3)";

/** The screen of every step. Its parent, and with it Back, is declared by the action store. */
const SCREENS: Record<RESOLVE_LABEL_ERROR_STEP, React.FC<IResolveLabelErrorScreenProps>> = {
    [RESOLVE_LABEL_ERROR_STEP.SELECT_RESOLUTION_PATH]: SelectResolutionPathScreen,
    [RESOLVE_LABEL_ERROR_STEP.VALIDATE_SHIPPING_DETAILS]: ValidateShippingDetailsScreen,
    [RESOLVE_LABEL_ERROR_STEP.SELECT_SHIPPING_RATE]: SelectShippingRateScreen,
    [RESOLVE_LABEL_ERROR_STEP.CHANGE_SHIPPING_OPTION]: ChangeShippingOptionScreen,
};

interface IProps {
    rmaAction: IRmaResolveLabelErrorAction;
    onCloseClick: () => void;
}

const ResolveLabelErrorModal: React.FC<IProps> = observer(({ rmaAction, onCloseClick }) => {
    const store = rmaAction.store;
    const Screen = SCREENS[store.currentStep];

    return (
        <DefaultModalLayout isCloseDisabled={false} onCloseClicked={onCloseClick}>
            <div className="resolve-label-error-modal">
                <div className="resolve-label-error-modal-header">
                    {store.canGoBack
                        ? (
                            <div className="resolve-label-error-modal-header-back" onClick={store.goBack}>
                                <Icon Component={IconBack} color={HEADER_ICON_COLOR} />
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>Back</FontBody2>
                            </div>
                        )
                        : <FontHeading4 color={FONT_COLOR.NEUTRAL12}>Validate shipment details</FontHeading4>}
                </div>

                <Screen store={store} />

                <div className="resolve-label-error-modal-footer">
                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                        Go to knowledge base and{" "}
                        <AHref href={KNOWLEDGE_BASE_URL} target="_blank">
                            <span className="resolve-label-error-modal-footer-link">
                                <FontBody2 color={FONT_COLOR.NEUTRAL3} variant={[FONT_VARIANT.BOLD]}>
                                    Read more
                                </FontBody2>
                                <IconNewTab color="var(--color-neutral-3)" />
                            </span>
                        </AHref>
                    </FontBody2>
                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                        Need help?{" "}
                        <AHref href={SUPPORT_URL} target="_blank">
                            <FontBody2 color={FONT_COLOR.NEUTRAL3} variant={[FONT_VARIANT.BOLD]}>
                                Contact support
                            </FontBody2>
                        </AHref>
                    </FontBody2>
                </div>
            </div>
        </DefaultModalLayout>
    );
});

export default ResolveLabelErrorModal;
